import _ from 'lodash'
import { useCallback, useEffect, useMemo } from 'react'
import { getAvailablePaymentMethods, getPaymentMethodById } from '@/lib/payment-methods'
import { formatCurrencyLabel, isValidDepositAmount } from '@/lib/deposit-validation'
import { resolveWalletDepositAddress } from '@/lib/deposit-admin-address'
import { showApiErrorToast } from '@/lib/api-toast'
import { useWalletProviders } from '@/hooks/use-wallet-providers'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { useCreateNowPaymentMutation, useGetBalancesQuery, useGetCurrenciesQuery, useLazyGetAdminAddressQuery } from '@/store/api/paymentApi'
import {
	resetDepositFlow,
	selectDepositAmount,
	selectDepositCurrencyCode,
	selectDepositMethodId,
	selectDepositMetamaskAddress,
	selectDepositNowPaymentData,
	selectDepositStep,
	setDepositAmount,
	setDepositCurrencyCode,
	setDepositMetamaskAddress,
	setDepositMethodId,
	setDepositNowPaymentData,
	setDepositStep,
} from '@/store/slices/paymentSlice'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import type { Currency, PaymentMethodId } from '@/types/payment'

export function useDeposit() {
	const dispatch = useAppDispatch()
	const isAuthenticated = useAppSelector(selectIsAuthenticated)
	const { connectedProviderIds } = useWalletProviders()
	const step = useAppSelector(selectDepositStep)
	const currencyCode = useAppSelector(selectDepositCurrencyCode)
	const methodId = useAppSelector(selectDepositMethodId)
	const amount = useAppSelector(selectDepositAmount)
	const nowPaymentData = useAppSelector(selectDepositNowPaymentData)
	const metamaskAddress = useAppSelector(selectDepositMetamaskAddress)

	const { data: balancesResponse, isLoading: isBalancesLoading } = useGetBalancesQuery(undefined, { skip: !isAuthenticated })
	const { data: currenciesResponse, isLoading: isCurrenciesLoading } = useGetCurrenciesQuery()
	const [createNowPayment, { isLoading: isCreatingNowPayment, error: createNowPaymentError, reset: resetCreateNowPayment }] = useCreateNowPaymentMutation()
	const [fetchAdminAddress, { isLoading: isFetchingAdminAddress, error: adminAddressError, reset: resetAdminAddress }] = useLazyGetAdminAddressQuery()

	const balances = balancesResponse?.data ?? []
	const currencies = currenciesResponse?.data ?? []

	const currency = useMemo(() => {
		const resolvedCode = currencyCode ?? _.find(balances, 'is_default')?.currency_code ?? _.first(balances)?.currency_code

		if (!resolvedCode) {
			return null
		}

		const balanceCurrency = _.find(balances, (balance) => balance.currency_code.toUpperCase() === resolvedCode.toUpperCase())?.currency
		if (balanceCurrency) {
			return balanceCurrency
		}

		return _.find(currencies, (item) => item.currency_code.toUpperCase() === resolvedCode.toUpperCase()) ?? null
	}, [balances, currencies, currencyCode])

	useEffect(() => {
		if (!currencyCode && currency) {
			dispatch(setDepositCurrencyCode(currency.currency_code))
		}
	}, [currency, currencyCode, dispatch])

	const selectedMethod = useMemo(() => (methodId ? getPaymentMethodById(methodId) : undefined), [methodId])
	const availableMethods = useMemo(() => getAvailablePaymentMethods(currency, connectedProviderIds), [connectedProviderIds, currency])

	useEffect(() => {
		if (!methodId) {
			return
		}

		const isMethodStillAvailable = availableMethods.some((method) => method.id === methodId)
		if (!isMethodStillAvailable) {
			dispatch(setDepositMethodId(null))
		}
	}, [availableMethods, dispatch, methodId])

	const currencyLabel = currency ? formatCurrencyLabel(currency) : ''
	const canProceedFromMethod = Boolean(methodId && selectedMethod && availableMethods.some((method) => method.id === methodId))
	const canProceedFromAmount = isValidDepositAmount(amount, currency)
	const isSubmitting = isCreatingNowPayment || isFetchingAdminAddress

	const selectMethod = useCallback(
		(nextMethodId: PaymentMethodId) => {
			dispatch(setDepositMethodId(nextMethodId))
		},
		[dispatch],
	)

	const updateAmount = useCallback(
		(nextAmount: string) => {
			dispatch(setDepositAmount(nextAmount))
		},
		[dispatch],
	)

	const goToMethodStep = useCallback(() => {
		dispatch(setDepositStep('method'))
	}, [dispatch])

	const goToAmountStep = useCallback(() => {
		if (!canProceedFromMethod) {
			return
		}

		dispatch(setDepositStep('amount'))
	}, [canProceedFromMethod, dispatch])

	const goBack = useCallback(() => {
		if (step === 'payment') {
			dispatch(setDepositNowPaymentData(null))
			dispatch(setDepositMetamaskAddress(null))
			resetCreateNowPayment()
			resetAdminAddress()
			dispatch(setDepositStep('amount'))
			return
		}

		if (step === 'amount') {
			dispatch(setDepositStep('method'))
		}
	}, [dispatch, resetAdminAddress, resetCreateNowPayment, step])

	const goToPaymentStep = useCallback(() => {
		dispatch(setDepositStep('payment'))
	}, [dispatch])

	const resetFlow = useCallback(() => {
		dispatch(resetDepositFlow())
		resetCreateNowPayment()
		resetAdminAddress()
	}, [dispatch, resetAdminAddress, resetCreateNowPayment])

	const submitDeposit = useCallback(async () => {
		if (!currency || !selectedMethod || !canProceedFromAmount || isSubmitting) {
			return
		}

		if (selectedMethod.provider === 'nowpayments') {
			try {
				const response = await createNowPayment({
					nowpayments_code: currency.nowpayments_code,
					amount,
				}).unwrap()

				dispatch(setDepositNowPaymentData(response.data))
				goToPaymentStep()
			} catch {
				// Global API toasts are handled by playerBaseQuery.
			}

			return
		}

		if (selectedMethod.provider === 'metamask' || selectedMethod.provider === 'phantom') {
			const provider = selectedMethod.provider
			const currencyAddress = resolveWalletDepositAddress(currency, provider)

			if (currencyAddress) {
				dispatch(setDepositMetamaskAddress(currencyAddress))
				goToPaymentStep()
				return
			}

			try {
				const response = await fetchAdminAddress().unwrap()
				const adminAddress = resolveWalletDepositAddress(currency, provider, response.data)

				if (!adminAddress) {
					showApiErrorToast('Admin deposit address is not configured for this currency.')
					return
				}

				dispatch(setDepositMetamaskAddress(adminAddress))
				goToPaymentStep()
			} catch {
				// Global API toasts are handled by playerBaseQuery.
			}
		}
	}, [amount, canProceedFromAmount, createNowPayment, currency, dispatch, fetchAdminAddress, goToPaymentStep, isSubmitting, selectedMethod])

	const retrySubmit = useCallback(() => {
		resetCreateNowPayment()
		resetAdminAddress()
		void submitDeposit()
	}, [resetAdminAddress, resetCreateNowPayment, submitDeposit])

	return {
		step,
		currency,
		currencyLabel,
		methodId,
		selectedMethod,
		availableMethods,
		amount,
		nowPaymentData,
		metamaskAddress,
		isBalancesLoading,
		isCurrenciesLoading,
		isSubmitting,
		canProceedFromMethod,
		canProceedFromAmount,
		createNowPaymentError,
		adminAddressError,
		selectMethod,
		updateAmount,
		goToMethodStep,
		goToAmountStep,
		goBack,
		submitDeposit,
		retrySubmit,
		resetFlow,
	}
}
